<template>
    <div class="cz2__review-views">
        <div class="cz2__review-views--wrapper">
            <span class="cz2__sr-only">
                {{ $t('SR_ACTIVATING') }}
            </span>

            <button
                :class="[
                    {
                        'cz2__review-views__view--active': selectedToggleView === 'Main',
                        'cz2__review-views__view--pop': popPreviewImage == null,
                    },
                    isTwoPack ? 'cz2__review-views__view--pop-pack' : 'cz2__review-views__view--pop-single',
                ]"
                type="button"
                @click="selectView('Main')"
                :aria-current="selectedToggleView === 'Main'"
                aria-label="View pop out of box"
                class="cz2__review-views__view"
            >
                <span
                    class="cz2__review-views__view-image"
                    :style="{
                        backgroundImage: popPreviewImage ? `url(${popPreviewImage})` : null,
                    }"
                />
            </button>

            <button
                :class="[
                    {
                        'cz2__review-views__view--active': isBoxView(selectedToggleView),
                        'cz2__review-views__view--box': boxPreviewImage == null,
                    },
                    isTwoPack ? 'cz2__review-views__view--box-pack' : 'cz2__review-views__view--box-single',
                ]"
                type="button"
                @click="selectView('Box')"
                :aria-current="isBoxView(selectedToggleView)"
                aria-label="View pop in box"
                class="cz2__review-views__view"
            >
                <span
                    class="cz2__review-views__view-image"
                    :style="{
                        backgroundImage: boxPreviewImage ? `url(${boxPreviewImage})` : null,
                    }"
                />
            </button>
        </div>
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__review-views {
            text-align: center;
        }

        .cz2__review-views--wrapper {
            display: flex;
            flex-direction: column;
            gap: 16px;
            align-items: center;
        }

        .cz2__review-views__view {
            border-radius: 15px;
            border: 1px solid color(pokemonBlueLight);
            background-color: white;
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;

            position: relative;

            width: 64px;
            height: 64px;
            padding: 4px;

            &:focus-visible {
                outline: none;

                border: 1px solid white;
            }

            &:not(.cz2__review-views__view--active) {
                .cz2__review-views__view-image {
                    opacity: 64%;
                }
            }

            .cz2__review-views__view-image {
                flex: none;
                width: 100%;
                height: 100%;

                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
            }

            &.cz2__review-views__view--pop {
                .cz2__review-views__view-image {
                    background: transparent url(../assets/images/figure@2x.png) no-repeat center/contain;
                }
            }

            &.cz2__review-views__view--box {
                .cz2__review-views__view-image {
                    background: transparent url(../assets/images/box@2x.png) no-repeat center/contain;
                }
            }

            &.cz2__review-views__view--active {
                background-color: color(pokemonBlueLight);
            }
        }

        // Special styling for the Name/Review combo screen only
        .cz2__steps-options__review,
        .cz2__steps-options__name {
            .cz2__review-views--wrapper {
                flex-direction: row;
            }

            @include respond-above(md) {
                .cz2__review-views--wrapper {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    align-items: center;
                }
            }
        }

        @include respond-above(lg) {
            .cz2__review-views--wrapper {
                gap: 10px;
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__review-views--wrapper {
            gap: 16px;

            .cz2__review-views {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
            }
        }

        .cz2__review-views__view {
            width: 80px;
            height: 80px;
        }
    }
</style>

<script>
    import * as constants from '../constants';

    export default {
        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            isTwoPack() {
                return this.$store.getters.isTwoPack;
            },

            pop2Started() {
                return Object.keys(this.$store.state.customizer.seenGroupsByController).length > 1;
            },

            currentGroupCode() {
                return this.$store.state.customizer.currentGroup?.code;
            },

            isNameStep() {
                return this.currentGroupCode === 'Name';
            },

            popPreviewImage() {
                return this.$store.state.customizer.popPreviewImage;
            },

            boxPreviewImage() {
                return this.$store.state.customizer.boxPreviewImage;
            },

            selectedToggleView() {
                return this.$store.state.selectedToggleView;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },
        },

        beforeMount() {
            this.getPreviews();
        },

        mounted() {
            this.emitter.$on('cameraLoading', this.cameraLoading);

            if (!this.isNameStep) {
                this.selectView(this.selectedToggleView);
            }
        },

        beforeDestroy() {
            this.emitter.$off('cameraLoading', this.cameraLoading);
        },

        methods: {
            selectView(view) {
                this.$store.dispatch('setSelectedToggleView', view);
            },

            async getPreviews() {
                const timeoutLength = this.isNameStep ? 500 : 0;

                if (this.previewDebounce) {
                    clearTimeout(this.previewDebounce);
                }

                this.previewDebounce = setTimeout(async () => {
                    const popImage = await this.$store.dispatch('snapshot', {
                        view: 'SnapshotPop',
                        onlyController: this.$store.state.customizer.viewController,
                    });

                    const boxImage = await this.$store.dispatch('snapshot', {
                        view: this.isTwoPack ? 'SnapshotBoxPack' : 'SnapshotBox',
                        // Render both pops.
                        onlyController: this.isTwoPack ? null : this.$store.state.customizer.viewController,
                    });

                    this.$store.dispatch('setPreviewImages', {
                        popPreviewImage: popImage,
                        boxPreviewImage: boxImage,
                    });
                }, timeoutLength);
            },

            cameraLoading(loading) {
                if (!loading) {
                    // If camera finished loading while we were on this step, recapture the screenshots.
                    this.getPreviews().then(() => {
                        // Don't care what happens when it finishes.
                    });
                }
            },

            isBoxView(view) {
                return constants.BOX_VIEWS.includes(view);
            },
        },
    };
</script>
